import { createBrowserRouter, Navigate } from 'react-router';
import { HomeGate, PublicOnly, SessionAware } from '../shared/auth/guards';
import { RouteError } from './RouteError';

/**
 * Route map mirrors the screen catalogue (docs/architecture/frontend.md). Every screen is a
 * lazy chunk so the first load only ships the shell and the current page.
 */
const page = (load: () => Promise<{ default: React.ComponentType }>) => async () => ({
  Component: (await load()).default,
});

export const router = createBrowserRouter([
  {
    errorElement: <RouteError />,
    children: [
      {
        element: <PublicOnly />,
        children: [
          { path: '/login', lazy: page(() => import('./pages/login')) },
          { path: '/register', lazy: page(() => import('./pages/register')) },
          { path: '/forgot-password', lazy: page(() => import('./pages/forgot-password')) },
          { path: '/reset-password', lazy: page(() => import('./pages/reset-password')) },
        ],
      },
      // Legal texts are public for everyone, signed in or not (GDPR/PDPL transparency).
      { path: '/privacy', lazy: page(() => import('./pages/privacy')) },
      { path: '/terms', lazy: page(() => import('./pages/terms')) },
      // Public read-only list (token in the URL is the credential): no session, no guards.
      { path: '/s/:token', lazy: page(() => import('./pages/public-list')) },
      {
        element: <SessionAware />,
        children: [
          { path: '/verify-email', lazy: page(() => import('./pages/verify-email')) },
          { path: '/invitations/:token', lazy: page(() => import('./pages/accept-invitation')) },
        ],
      },
      // "/" is the landing page for visitors and the app for signed-in users; every route
      // below it is private (HomeGate sends visitors to the login page).
      {
        path: '/',
        element: <HomeGate />,
        children: [
          { index: true, lazy: page(() => import('./pages/dashboard')) },
          { path: 'lists/:listId', lazy: page(() => import('./pages/list')) },
          { path: 'categories', lazy: page(() => import('./pages/categories')) },
          { path: 'categories/:categoryId', lazy: page(() => import('./pages/category')) },
          { path: 'search', lazy: page(() => import('./pages/search')) },
          { path: 'favorites', lazy: page(() => import('./pages/favorites')) },
          // The Shared page became filters on the Lists page; old links and emails land there.
          { path: 'shared', element: <Navigate to="/?filter=shared" replace /> },
          // Archived lists are a filter on the Lists page; old links land there.
          { path: 'archived', element: <Navigate to="/?filter=archived" replace /> },
          { path: 'meals', lazy: page(() => import('./pages/meals')) },
          {
            path: 'meals/cuisine/:cuisineId',
            lazy: page(() => import('./pages/cuisine-meals')),
          },
          { path: 'meals/:mealId', lazy: page(() => import('./pages/meal')) },
          { path: 'sponsored/:sponsoredId', lazy: page(() => import('./pages/sponsored-list')) },
          { path: 'notifications', lazy: page(() => import('./pages/notifications')) },
          { path: 'admin', lazy: page(() => import('./pages/admin')) },
          { path: 'admin/sponsored', lazy: page(() => import('./pages/admin-sponsored')) },
          { path: 'admin/catalog', lazy: page(() => import('./pages/admin-catalog')) },
          { path: 'profile', lazy: page(() => import('./pages/profile')) },
          { path: 'settings', lazy: page(() => import('./pages/settings')) },
          { path: '*', lazy: page(() => import('./pages/not-found')) },
        ],
      },
    ],
  },
]);
